import { useState, useRef, useCallback, useEffect, type ChangeEvent } from 'react';
import { Paperclip, Loader2 } from 'lucide-react';
import { useBackend } from '@/services/backend';
import { useTerminalStore } from '@/stores/terminalStore';
import { logger } from '@/lib/logger';
import { uint8ToBase64 } from '@/lib/base64';

const TAG = 'TerminalAttach';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB — matches Rust limit

type AttachState = 'idle' | 'uploading' | 'error';

interface TerminalAttachButtonProps {
  activeSessionId: number | null;
  projectPath: string;
}

export default function TerminalAttachButton({ activeSessionId, projectPath }: TerminalAttachButtonProps) {
  const backend = useBackend();
  const requestFocus = useTerminalStore((s) => s.requestFocus);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, setState] = useState<AttachState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const showError = useCallback((msg: string) => {
    setState('error');
    setErrorMsg(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => {
      setState('idle');
      setErrorMsg('');
    }, 3000);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Size guard — check before reading
    if (file.size > MAX_FILE_SIZE) {
      showError('File too large (max 10MB)');
      e.target.value = '';
      requestFocus();
      return;
    }

    setState('uploading');

    try {
      // Read file as base64
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      // Save via backend (works over WebRTC — chunking handles large files)
      const savedPath = await backend.files.saveAttachment(projectPath, file.name, base64);

      // Inject the file path into the terminal
      if (activeSessionId !== null) {
        const encoder = new TextEncoder();
        const pathBytes = encoder.encode(savedPath + ' ');
        const encoded = uint8ToBase64(pathBytes);
        await backend.terminal.write(activeSessionId, encoded);
      }

      setState('idle');
      requestFocus();
    } catch (err) {
      logger.warn(TAG, 'Attachment upload failed', err);
      showError(err instanceof Error ? err.message : 'Upload failed');
      requestFocus();
    }

    // Reset input so same file can be re-selected
    e.target.value = '';
  }, [backend, projectPath, activeSessionId, showError, requestFocus]);

  // Restore focus when the file picker is cancelled without selecting
  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) return;
    const onCancel = () => requestFocus();
    input.addEventListener('cancel', onCancel);
    return () => input.removeEventListener('cancel', onCancel);
  }, [requestFocus]);

  const noTerminal = activeSessionId === null;
  const disabled = noTerminal || state === 'uploading';

  let icon: React.ReactNode;
  let className = 'flex items-center justify-center h-6 px-1.5 rounded transition-colors ';
  let title = '';

  switch (state) {
    case 'error':
      icon = <Paperclip className="w-3.5 h-3.5" />;
      className += 'text-red-500 cursor-default';
      title = errorMsg;
      break;
    case 'uploading':
      icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
      className += 'text-amber-400 cursor-default';
      title = 'Uploading...';
      break;
    default: // idle
      icon = <Paperclip className="w-3.5 h-3.5" />;
      if (noTerminal) {
        className += 'text-stone-600 opacity-50 cursor-default';
        title = 'No active terminal';
      } else {
        className += 'text-base-content/60 hover:text-base-content hover:bg-base-content/10 cursor-pointer';
        title = 'Attach file';
      }
      break;
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf,text/*"
        className="hidden"
        onChange={handleFileSelected}
      />
      <button
        onClick={disabled && state !== 'error' ? undefined : handleClick}
        disabled={disabled && state !== 'error'}
        className={className}
        title={title}
      >
        {icon}
      </button>
    </>
  );
}
